<script lang="ts">
	/**
	 * Social profile links, driven by `$lib/data/social`.
	 *
	 * Icons are inline SVG paths rather than an icon font or remote images: three
	 * marks do not justify a dependency, and inline means they inherit colour and
	 * cannot fail to load.
	 */
	import { SOCIAL_LINKS } from '$lib/data/social';

	let {
		size = 'md',
		tone = 'default'
	}: { size?: 'sm' | 'md'; tone?: 'default' | 'inverse' } = $props();

	/*
	 * Rendered as chips, not bare glyphs. Three 16px marks in `text-muted` with
	 * no ground read as punctuation rather than as links — a filled circle that
	 * takes the brand colour on hover is the affordance the utility bar and the
	 * footer were both missing.
	 */
	const box = $derived(size === 'sm' ? 'h-4 w-4' : 'h-[1.125rem] w-[1.125rem]');
	const chip = $derived(size === 'sm' ? 'h-7 w-7' : 'h-9 w-9');
	const colour = $derived(
		tone === 'inverse'
			? 'bg-white/10 text-white hover:bg-brand-500 hover:text-ink-500'
			: 'bg-surface-muted text-body hover:bg-brand-500 hover:text-ink-500'
	);
</script>

<ul class="flex items-center gap-2">
	{#each SOCIAL_LINKS as link (link.id)}
		<li>
			<a
				href={link.href}
				target="_blank"
				rel="noopener noreferrer"
				aria-label={link.label}
				title={link.label}
				class="inline-flex items-center justify-center rounded-full transition-colors {chip} {colour}"
			>
				{#if link.id === 'line'}
					<svg class={box} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
						<path d="M24 10.3C24 4.94 18.62.58 12 .58S0 4.94 0 10.3c0 4.8 4.26 8.82 10.02 9.58.39.09.92.26 1.06.6.12.3.08.78.04 1.09l-.17 1.03c-.05.3-.24 1.19 1.05.65s6.95-4.09 9.48-7A8.6 8.6 0 0 0 24 10.3ZM7.75 13.4H5.37a.63.63 0 0 1-.63-.63V8.02a.63.63 0 0 1 1.26 0v4.12h1.75a.63.63 0 0 1 0 1.26Zm2.47-.63a.63.63 0 0 1-1.26 0V8.02a.63.63 0 0 1 1.26 0v4.75Zm5.72 0a.63.63 0 0 1-1.13.38l-2.44-3.32v2.94a.63.63 0 0 1-1.26 0V8.02a.63.63 0 0 1 1.13-.38l2.44 3.32V8.02a.63.63 0 0 1 1.26 0v4.75Zm3.84-3a.63.63 0 0 1 0 1.26h-1.75v1.12h1.75a.63.63 0 0 1 0 1.26h-2.38a.63.63 0 0 1-.63-.63V8.02a.63.63 0 0 1 .63-.63h2.38a.63.63 0 0 1 0 1.26h-1.75v1.12h1.75Z" />
					</svg>
				{:else if link.id === 'facebook'}
					<svg class={box} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
						<path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.5h-2.8V24C19.61 23.1 24 18.1 24 12.07Z" />
					</svg>
				{:else}
					<svg class={box} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
						<path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.3-1.46.72-2.12 1.38C1.36 2.67.95 3.34.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.3.79.72 1.46 1.38 2.12.66.66 1.33 1.07 2.12 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.3 1.46-.72 2.12-1.38.66-.66 1.07-1.33 1.38-2.12.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.3-.79-.72-1.46-1.38-2.12C21.33 1.36 20.66.95 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm7.85-10.4a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0Z" />
					</svg>
				{/if}
			</a>
		</li>
	{/each}
</ul>
